import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { MessageSquare, Send, Search, User, Loader2, Inbox, ArrowLeft, Smartphone, ShieldOff, FileText, Download, Megaphone, X, Paperclip, Mic, Square, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  listConversations,
  getConversation,
  sendMessage as apiSend,
  markRead,
  setAppAccess,
  broadcastMessage,
  uploadBroadcastFile,
  uploadMessageFile,
  FirmConversation,
  FirmClientMessage,
} from '@/services/api/firm-client-messages';

function relativeTime(iso?: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  if (sec < 604800) return `${Math.round(sec / 86400)}d`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function initials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
}

export default function ClientMessagesPage() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [showListOnMobile, setShowListOnMobile] = useState(true);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  // The surrounding panel chrome (top menu bar + ribbon + main padding) has a
  // variable height, so a fixed `100vh - 80px` guess overflows and pushes the
  // composer below the viewport. Measure the real top offset and fill exactly
  // to the viewport bottom so the messages list scrolls and the composer pins.
  const rootRef = useRef<HTMLDivElement>(null);
  const [rootH, setRootH] = useState<number>();
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top;
      setRootH(Math.max(360, Math.round(window.innerHeight - top)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const { data: conversations = [], isLoading: convLoading } = useQuery({
    queryKey: ['firm-conversations'],
    queryFn: listConversations,
    refetchInterval: 15_000,
    staleTime: 5_000,
  });

  const filtered = conversations.filter(c =>
    !searchText.trim() ||
    c.displayName.toLowerCase().includes(searchText.toLowerCase()) ||
    (c.phone || '').toLowerCase().includes(searchText.toLowerCase())
  );

  // Auto-select first conversation when list loads
  useEffect(() => {
    if (!selectedId && filtered.length > 0) setSelectedId(filtered[0].clientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length]);

  return (
    <div
      ref={rootRef}
      className="flex flex-col -mx-4 -mb-4 md:-mx-6 md:-mb-6"
      style={{ height: rootH ? `${rootH}px` : 'calc(100vh - 130px)' }}
    >
      <div className="px-6 pt-4 pb-3 border-b flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-primary" />
            Client messages
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Replies to and from clients on the mobile app.</p>
        </div>
        <button
          onClick={() => setBroadcastOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 whitespace-nowrap"
        >
          <Megaphone className="w-4 h-4" /> Send to all clients
        </button>
      </div>

      {broadcastOpen && (
        <BroadcastDialog
          onClose={() => setBroadcastOpen(false)}
          onSent={() => { setBroadcastOpen(false); qc.invalidateQueries({ queryKey: ['firm-conversations'] }); }}
        />
      )}

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* List pane */}
        <aside
          className={cn(
            'w-full md:w-80 lg:w-96 border-r flex flex-col bg-card',
            !showListOnMobile && 'hidden md:flex',
          )}
        >
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search clients..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-md border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {convLoading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-muted-foreground">
                {searchText ? 'No matches' : 'No active clients yet'}
              </div>
            ) : (
              <ul className="divide-y">
                {filtered.map(c => (
                  <ConversationRow
                    key={c.clientId}
                    conv={c}
                    selected={selectedId === c.clientId}
                    onSelect={() => { setSelectedId(c.clientId); setShowListOnMobile(false); }}
                  />
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Detail pane */}
        <section
          className={cn(
            'flex-1 flex flex-col min-w-0 min-h-0 bg-background',
            showListOnMobile && 'hidden md:flex',
          )}
        >
          {selectedId ? (
            <ConversationDetail
              clientId={selectedId}
              onBack={() => setShowListOnMobile(true)}
              onSent={() => qc.invalidateQueries({ queryKey: ['firm-conversations'] })}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
              <Inbox className="w-10 h-10 opacity-50" />
              <p className="text-sm">Pick a client to view the conversation</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ConversationRow({ conv, selected, onSelect }: { conv: FirmConversation; selected: boolean; onSelect: () => void }) {
  const hasUnread = conv.firmUnread > 0;
  return (
    <li>
      <button
        onClick={onSelect}
        className={cn(
          'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
          selected && 'bg-muted',
        )}
      >
        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
          {initials(conv.displayName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className={cn('text-sm truncate', hasUnread ? 'font-semibold text-foreground' : 'font-medium')}>
              {conv.displayName}
            </span>
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">{relativeTime(conv.lastAt)}</span>
          </div>
          {conv.managerName && (
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground truncate">
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">{conv.managerName}</span>
            </div>
          )}
          <div className="flex items-baseline justify-between gap-2 mt-0.5">
            <span dir={dirOf(conv.lastMessage)} className={cn('text-xs truncate', hasUnread ? 'text-foreground' : 'text-muted-foreground')}>
              {conv.lastDirection === 'outbound' && <span className="opacity-60">You: </span>}
              {conv.lastMessage || (conv.total === 0 ? 'No messages yet' : '')}
            </span>
            {hasUnread && (
              <span className="shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                {conv.firmUnread}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function ConversationDetail({ clientId, onBack, onSent }: { clientId: string; onBack: () => void; onSent: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [recState, setRecState] = useState<'idle' | 'recording'>('idle');
  const [recSecs, setRecSecs] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<number | null>(null);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        const ext = (mime.includes('mp4') ? 'm4a' : 'webm');
        setAttachment(new File([blob], `voice-message-${Date.now()}.${ext}`, { type: blob.type }));
        stream.getTracks().forEach(tk => tk.stop());
      };
      rec.start();
      recRef.current = rec;
      setRecSecs(0);
      setRecState('recording');
      tickRef.current = window.setInterval(() => setRecSecs(s => s + 1), 1000);
    } catch (e: any) {
      toast.error(e?.message || 'Microphone access denied');
    }
  };
  const stopRec = () => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecState('idle');
  };

  const { data, isLoading } = useQuery({
    queryKey: ['firm-conversation', clientId],
    queryFn: () => getConversation(clientId),
    refetchInterval: 10_000,
  });

  // Mark inbound as read when conversation opens
  useEffect(() => {
    if (!data) return;
    const hasUnread = data.messages.some(m => m.direction === 'inbound' && !m.firmReadAt);
    if (hasUnread) {
      markRead(clientId).then(() => {
        qc.invalidateQueries({ queryKey: ['firm-conversations'] });
      }).catch(() => {});
    }
  }, [clientId, data?.messages.length]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data?.messages.length]);

  const sendMut = useMutation({
    mutationFn: async (body: string) => {
      let att = undefined;
      if (attachment) att = await uploadMessageFile(clientId, attachment);
      return apiSend(clientId, body, att);
    },
    onSuccess: () => {
      setDraft('');
      setAttachment(null);
      qc.invalidateQueries({ queryKey: ['firm-conversation', clientId] });
      qc.invalidateQueries({ queryKey: ['firm-conversations'] });
      onSent();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to send'),
  });
  const submit = () => {
    const t = draft.trim();
    if ((!t && !attachment) || sendMut.isPending || recState === 'recording') return;
    sendMut.mutate(t);
  };
  const isAudio = attachment?.type.startsWith('audio/');
  const isImage = attachment?.type.startsWith('image/');

  if (isLoading || !data) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 border-b flex items-center gap-3 bg-card">
        <button onClick={onBack} className="md:hidden p-1.5 rounded hover:bg-muted">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
          {initials(data.client.displayName)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{data.client.displayName}</p>
          {data.client.managerName ? (
            <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
              <User className="w-3 h-3 shrink-0" />
              <span className="truncate">{data.client.managerName}</span>
              <span className="opacity-60 whitespace-nowrap">· {data.client.phone || data.client.email || '-'}</span>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground truncate">{data.client.phone || data.client.email || '-'}</p>
          )}
        </div>
        <AppAccessToggle
          clientId={data.client.id}
          enabled={data.client.appAccessEnabled}
          phone={data.client.phone}
        />
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
        {data.messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
            <MessageSquare className="w-10 h-10 opacity-40" />
            <p className="text-sm">No messages yet - start the conversation below.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {data.messages.map(m => <MessageBubble key={m.id} m={m} />)}
          </ul>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={e => { e.preventDefault(); submit(); }}
        className="border-t bg-card px-4 md:px-6 py-3"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setAttachment(f); }}
        />

        {/* Attachment / recording preview */}
        {attachment ? (
          <div className="mb-2 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
            {isAudio ? <Mic className="w-4 h-4 text-primary shrink-0" />
              : isImage ? <ImageIcon className="w-4 h-4 text-primary shrink-0" />
              : <FileText className="w-4 h-4 text-primary shrink-0" />}
            <span className="text-sm truncate flex-1">{isAudio ? 'Voice message' : attachment.name}</span>
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">{(attachment.size / 1024).toFixed(0)} KB</span>
            <button type="button" onClick={() => setAttachment(null)} className="text-muted-foreground hover:text-rose-600" aria-label="Remove attachment">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : recState === 'recording' ? (
          <div className="mb-2 flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 px-3 py-2">
            <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-sm font-mono tabular-nums text-rose-700 dark:text-rose-300 flex-1">
              {Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, '0')}
            </span>
            <button type="button" onClick={stopRec} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-600 text-white text-xs">
              <Square className="w-3 h-3 fill-current" /> Stop
            </button>
          </div>
        ) : null}

        <div className="flex items-end gap-2">
          {recState !== 'recording' && !attachment && (
            <>
              <button type="button" onClick={() => fileInputRef.current?.click()} title="Attach image / PDF"
                className="inline-flex items-center justify-center rounded-lg border h-9 w-9 text-muted-foreground hover:bg-muted">
                <Paperclip className="w-4 h-4" />
              </button>
              <button type="button" onClick={startRec} title="Record voice"
                className="inline-flex items-center justify-center rounded-lg border h-9 w-9 text-muted-foreground hover:bg-muted">
                <Mic className="w-4 h-4" />
              </button>
            </>
          )}
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
            placeholder={attachment ? 'Add a caption (optional)…' : 'Type a message...'}
            rows={1}
            className="flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary max-h-32"
          />
          <button
            type="submit"
            disabled={(!draft.trim() && !attachment) || sendMut.isPending || recState === 'recording'}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors"
          >
            {sendMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">Enter to send · Shift+Enter for newline · attach a file or record a voice note</p>
      </form>
    </div>
  );
}

function AppAccessToggle({ clientId, enabled, phone }: { clientId: string; enabled: boolean; phone?: string | null }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (next: boolean) => setAppAccess(clientId, next),
    onSuccess: (_d, next) => {
      qc.invalidateQueries({ queryKey: ['firm-conversation', clientId] });
      qc.invalidateQueries({ queryKey: ['firm-conversations'] });
      toast.success(next ? 'App access granted' : 'App access revoked');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to update'),
  });
  const noPhone = !phone;
  return (
    <div className="flex items-center gap-2">
      <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground">
        {enabled
          ? <><Smartphone className="w-3.5 h-3.5 text-green-600" /> App access</>
          : <><ShieldOff className="w-3.5 h-3.5 text-muted-foreground" /> No app access</>}
      </div>
      <button
        type="button"
        onClick={() => !mut.isPending && !noPhone && mut.mutate(!enabled)}
        disabled={mut.isPending || noPhone}
        title={noPhone ? 'Add a phone number first' : (enabled ? 'Revoke app access' : 'Grant app access')}
        aria-pressed={enabled}
        className={cn(
          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50',
          enabled ? 'bg-primary' : 'bg-muted-foreground/30',
        )}
      >
        <span
          className={cn(
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
            enabled ? 'translate-x-5' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  );
}

// If a message contains any Persian/Arabic letter, render it right-to-left.
const RTL_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
const dirOf = (text?: string | null): 'rtl' | 'ltr' => (text && RTL_RE.test(text) ? 'rtl' : 'ltr');

function pickAudioMime(): string {
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m;
  }
  return '';
}

function BroadcastDialog({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [text, setText] = useState('');
  const [target, setTarget] = useState<'app' | 'all'>('app');
  const [confirming, setConfirming] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [recState, setRecState] = useState<'idle' | 'recording'>('idle');
  const [recSecs, setRecSecs] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<number | null>(null);

  const isAudio = attachment?.type.startsWith('audio/');
  const isImage = attachment?.type.startsWith('image/');

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        const ext = (mime.includes('mp4') ? 'm4a' : 'webm');
        setAttachment(new File([blob], `voice-message-${Date.now()}.${ext}`, { type: blob.type }));
        stream.getTracks().forEach(t => t.stop());
      };
      rec.start();
      recRef.current = rec;
      setRecSecs(0);
      setRecState('recording');
      tickRef.current = window.setInterval(() => setRecSecs(s => s + 1), 1000);
    } catch (e: any) {
      toast.error(e?.message || 'Microphone access denied');
    }
  };
  const stopRec = () => {
    if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecState('idle');
  };

  const mut = useMutation({
    mutationFn: async () => {
      let att = undefined;
      if (attachment) att = await uploadBroadcastFile(attachment);
      return broadcastMessage(text.trim(), target, att);
    },
    onSuccess: (res) => {
      toast.success(`Sent to ${res.sent} client${res.sent === 1 ? '' : 's'}`);
      onSent();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Broadcast failed'),
  });

  const canSend = (text.trim().length > 0 || !!attachment) && recState === 'idle' && !mut.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3.5">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-primary" /> Send to all clients
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            This message goes to every selected client's app inbox at once.
          </p>

          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            dir={dirOf(text)}
            style={{ textAlign: dirOf(text) === 'rtl' ? 'right' : 'left' }}
            rows={4}
            placeholder="Type your announcement (optional if attaching a file)…"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
          />

          {/* Attachment row */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setAttachment(f); }}
          />
          {attachment ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
              {isAudio ? <Mic className="w-4 h-4 text-primary shrink-0" />
                : isImage ? <ImageIcon className="w-4 h-4 text-primary shrink-0" />
                : <FileText className="w-4 h-4 text-primary shrink-0" />}
              <span className="text-sm truncate flex-1">{attachment.name}</span>
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">{(attachment.size / 1024).toFixed(0)} KB</span>
              <button onClick={() => setAttachment(null)} className="text-muted-foreground hover:text-rose-600" aria-label="Remove attachment">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : recState === 'recording' ? (
            <div className="flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 px-3 py-2">
              <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-sm font-mono tabular-nums text-rose-700 dark:text-rose-300 flex-1">
                {Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, '0')}
              </span>
              <button onClick={stopRec} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-600 text-white text-xs">
                <Square className="w-3 h-3 fill-current" /> Stop
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs hover:bg-muted">
                <Paperclip className="w-3.5 h-3.5" /> Attach image / PDF
              </button>
              <button onClick={startRec} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs hover:bg-muted">
                <Mic className="w-3.5 h-3.5" /> Record voice
              </button>
            </div>
          )}

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Recipients</p>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={target === 'app'} onChange={() => setTarget('app')} />
                Clients with app access (they can read it now)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={target === 'all'} onChange={() => setTarget('all')} />
                All active clients (stored for those without app access)
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md border text-sm hover:bg-muted">Cancel</button>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              disabled={!canSend}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <Megaphone className="w-3.5 h-3.5" /> Review & send
            </button>
          ) : (
            <button
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-60"
            >
              {mut.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</> : <>Confirm - send to {target === 'app' ? 'app clients' : 'all clients'}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m }: { m: FirmClientMessage }) {
  const isFirm = m.direction === 'outbound';
  const isImage = m.attachmentContentType?.startsWith('image/');
  const contentDir = dirOf(m.body);
  return (
    <li className={cn('flex gap-2', isFirm ? 'justify-end' : 'justify-start')}>
      {!isFirm && (
        <div className="w-7 h-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
          <User className="w-3.5 h-3.5" />
        </div>
      )}
      <div className={cn('flex flex-col gap-0.5 max-w-[75%]', isFirm ? 'items-end' : 'items-start')}>
        {isFirm && m.senderName && (
          <span className="text-[10px] text-muted-foreground">{m.senderName}</span>
        )}
        {m.attachmentUrl && isImage && (
          <a href={m.attachmentUrl} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={m.attachmentUrl}
              alt={m.attachmentName || 'attachment'}
              className="max-w-full rounded-2xl border bg-card cursor-zoom-in max-h-72 object-cover"
            />
          </a>
        )}
        {m.attachmentUrl && m.attachmentContentType?.startsWith('audio/') && (
          <audio src={m.attachmentUrl} controls className="max-w-full" />
        )}
        {m.attachmentFileId && !isImage && !m.attachmentContentType?.startsWith('audio/') && (
          <a
            href={m.attachmentUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm max-w-full',
              isFirm ? 'bg-primary text-primary-foreground border-primary-foreground/30' : 'bg-card hover:bg-muted/50',
            )}
          >
            <FileText className="w-4 h-4 shrink-0" />
            <span className="truncate flex-1">{m.attachmentName || 'attachment'}</span>
            <Download className="w-3.5 h-3.5 shrink-0 opacity-70" />
          </a>
        )}
        {m.body && (
          <div
            dir={contentDir}
            style={{ textAlign: contentDir === 'rtl' ? 'right' : 'left' }}
            className={cn(
              'rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words',
              isFirm ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted text-foreground rounded-bl-md',
            )}
          >
            {m.body}
          </div>
        )}
        <span className="text-[10px] text-muted-foreground">
          {new Date(m.createdAt).toLocaleString('en-AU', { hour: 'numeric', minute: '2-digit', day: 'numeric', month: 'short' })}
        </span>
      </div>
    </li>
  );
}
